/**
 * Sidebar icon set.
 *
 * The nav previously drew ~50 destinations from about twenty glyphs, so the
 * same building stood for Companies, News, KYC, Bank wire details, Agent
 * performance and Asset management at once, and three unrelated tabs shared a
 * headphone. An icon that appears six times carries no information — the eye
 * cannot use it to tell rows apart, which is most of what an icon is for in a
 * list this long.
 *
 * These are one line-drawn glyph per destination, all on the same 24px grid
 * and the same 1.7 stroke so they read as one family.
 *
 * ⚠️ They are STROKE-based, unlike the old fill-based SVGs. `CustomLink`'s
 * stylesheet colours icons with `svg path { fill: … }`, which would turn a
 * stroked outline into a solid blob, so every glyph is tagged `data-glyph` and
 * the nav stylesheet uses that hook to force `fill: none` and colour the
 * stroke with `currentColor` instead.
 */

const Glyph = ({d, children}) => (
  <svg
    data-glyph=""
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    {d ? <path d={d} /> : children}
  </svg>
);

/* ---------- top level ---------------------------------------------------- */

export const HomeGlyph = () => (
  <Glyph d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5Z" />
);

export const AnalyticsGlyph = () => (
  <Glyph>
    <path d="M3 3v16a2 2 0 0 0 2 2h16" />
    <path d="m7 14 3.5-4 3 2.5L20 6" />
  </Glyph>
);

export const SupportGlyph = () => (
  <Glyph>
    <path d="M4 13a8 8 0 0 1 16 0" />
    <path d="M4 13v3a2 2 0 0 0 2 2h1v-5H6a2 2 0 0 0-2 2Zm16 0v3a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2Z" />
    <path d="M17 18v1a2 2 0 0 1-2 2h-2" />
  </Glyph>
);

export const AutomationGlyph = () => (
  <Glyph>
    <rect x="7" y="8" width="10" height="9" rx="2" />
    <path d="M12 5V8M9.5 12h.01M14.5 12h.01M10 20h4M4 11v3M20 11v3" />
  </Glyph>
);

export const UsersGlyph = () => (
  <Glyph>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.5a3 3 0 0 1 0 5.6M17.5 20a5.5 5.5 0 0 0-2.2-4.4" />
  </Glyph>
);

export const AssetsGlyph = () => (
  <Glyph>
    <path d="M3 20h18" />
    <path d="M5 20V9l5-3 5 3v11" />
    <path d="M15 20V12l4 2v6" />
    <path d="M8 12h1.5M8 16h1.5" />
  </Glyph>
);

export const TradingGlyph = () => (
  <Glyph>
    <path d="M4 20V4" />
    <path d="M8 16v-4M12 16V7M16 16v-6M20 16v-9" />
    <path d="M4 20h17" />
  </Glyph>
);

export const FinancialsGlyph = () => (
  <Glyph>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h4" />
  </Glyph>
);

export const DividendsGlyph = () => (
  <Glyph>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M12 7.5v9M14.2 9.6a2.4 2.4 0 0 0-4.4 1.2c0 2.4 4.4 1.4 4.4 3.6a2.4 2.4 0 0 1-4.4 1.2" />
  </Glyph>
);

export const WalletGlyph = () => (
  <Glyph>
    <path d="M3 8a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2" />
    <rect x="3" y="8" width="18" height="12" rx="2" />
    <path d="M16.5 14h.01" />
  </Glyph>
);

export const NotificationGlyph = () => (
  <Glyph>
    <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6Z" />
    <path d="M10.5 19a2 2 0 0 0 3 0" />
  </Glyph>
);

export const ManagementGlyph = () => (
  <Glyph>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
    <path d="M3 12h18" />
  </Glyph>
);

export const FaqGlyph = () => (
  <Glyph>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M9.8 9.6a2.3 2.3 0 0 1 4.4.8c0 1.6-2.2 1.9-2.2 3.3" />
    <path d="M12 17h.01" />
  </Glyph>
);

export const CirclesGlyph = () => (
  <Glyph>
    <circle cx="9" cy="12" r="5" />
    <circle cx="15" cy="12" r="5" />
  </Glyph>
);

export const AgentHubGlyph = () => (
  <Glyph>
    <circle cx="12" cy="7.5" r="3" />
    <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M19 4.5 21 6l-2 1.5" />
  </Glyph>
);

export const AnnouncementGlyph = () => (
  <Glyph>
    <path d="M4 10v4a1 1 0 0 0 1 1h3l6 4V5L8 9H5a1 1 0 0 0-1 1Z" />
    <path d="M18 9.5a4 4 0 0 1 0 5" />
  </Glyph>
);

export const MarketingGlyph = () => (
  <Glyph>
    <path d="M12 3v18" />
    <path d="M5 8h9l3-3v10l-3-3H5a2 2 0 0 1 0-4Z" />
  </Glyph>
);

export const ListingsGlyph = () => (
  <Glyph>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 9h6M7 13h10M7 17h4" />
  </Glyph>
);

export const SettingsGlyph = () => (
  <Glyph>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a2 2 0 1 1-4 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3.5 15H3a2 2 0 1 1 0-4h.2A1.6 1.6 0 0 0 4.3 8.2l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 10 4.3V4a2 2 0 1 1 4 0v.2a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.3 1Z" />
  </Glyph>
);

export const SubscriptionGlyph = () => (
  <Glyph>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M2.5 10h19" />
    <path d="M6.5 15h3" />
  </Glyph>
);

export const SavingsGlyph = () => (
  <Glyph>
    <path d="M4 12a6 6 0 0 1 6-6h2.5a6 6 0 0 1 5.9 5H20a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-1.4a6 6 0 0 1-2.6 3V20h-3v-1h-2v1H8v-2A6 6 0 0 1 4 12Z" />
    <path d="M8.5 11h.01" />
  </Glyph>
);

export const TopicsGlyph = () => (
  <Glyph>
    <path d="M4 6h8M4 12h11M4 18h6" />
    <circle cx="18" cy="6" r="2" />
    <circle cx="19.5" cy="18" r="2" />
  </Glyph>
);

export const ReportGlyph = () => (
  <Glyph>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
    <path d="M9 14h6M9 17h4" />
  </Glyph>
);

export const PlansGlyph = () => (
  <Glyph>
    <path d="m20.6 12.6-8 8a2 2 0 0 1-2.8 0l-6.4-6.4a2 2 0 0 1-.6-1.4V5a2 2 0 0 1 2-2h7.8a2 2 0 0 1 1.4.6l6.6 6.6a2 2 0 0 1 0 2.4Z" />
    <path d="M7.5 7.5h.01" />
  </Glyph>
);

/* ---------- Users group -------------------------------------------------- */

export const UserListGlyph = () => (
  <Glyph>
    <circle cx="7" cy="8" r="2.6" />
    <path d="M3 18a4 4 0 0 1 8 0" />
    <path d="M14 8h7M14 12h7M14 16h5" />
  </Glyph>
);

export const KycGlyph = () => (
  <Glyph>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="8.5" cy="11" r="2" />
    <path d="M5.5 16.5a3.4 3.4 0 0 1 6 0" />
    <path d="M14.5 10h4M14.5 13.5h2.5" />
  </Glyph>
);

export const KybGlyph = () => (
  <Glyph>
    <path d="M4 20.5V6.2a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v14.3" />
    <path d="M13 10.5h6a1 1 0 0 1 1 1v9" />
    <path d="M2.5 20.5h19" />
    <path d="M7 9h3M7 12.5h3M7 16h3M16 14h1M16 17h1" />
  </Glyph>
);

export const WaitListGlyph = () => (
  <Glyph>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M12 7.5V12l3 1.8" />
  </Glyph>
);

export const FeedbackGlyph = () => (
  <Glyph>
    <path d="M20 14a2 2 0 0 1-2 2H8l-4 3.5V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8Z" />
    <path d="M9 10h.01M12 10h.01M15 10h.01" />
  </Glyph>
);

export const LeaderboardGlyph = () => (
  <Glyph>
    <path d="M4 20h16" />
    <rect x="4" y="12" width="4.5" height="8" rx="1" />
    <rect x="9.75" y="7" width="4.5" height="13" rx="1" />
    <rect x="15.5" y="15" width="4.5" height="5" rx="1" />
  </Glyph>
);

export const QuickActionsGlyph = () => (
  <Glyph d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5Z" />
);

/* ---------- Multi Asset group -------------------------------------------- */

export const InstrumentsGlyph = () => (
  <Glyph>
    <rect x="3" y="4" width="7" height="7" rx="1.5" />
    <rect x="14" y="4" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="6" rx="1.5" />
    <rect x="14" y="14" width="7" height="6" rx="1.5" />
  </Glyph>
);

export const ExchangeGlyph = () => (
  <Glyph>
    <path d="M4 8h13l-3-3M20 16H7l3 3" />
  </Glyph>
);

export const AssetClassGlyph = () => (
  <Glyph>
    <path d="M12 3 3 8l9 5 9-5-9-5Z" />
    <path d="m3 13 9 5 9-5" />
  </Glyph>
);

export const CompaniesGlyph = () => (
  <Glyph>
    <path d="M3 21h18" />
    <path d="M5 21V6a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v15" />
    <path d="M14 21V11h4a1 1 0 0 1 1 1v9" />
    <path d="M8 9h3M8 13h3M8 17h3" />
  </Glyph>
);

export const KeyStatsGlyph = () => (
  <Glyph>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 12V4a8 8 0 0 1 7 4l-7 4Z" />
  </Glyph>
);

export const CollectionsGlyph = () => (
  <Glyph>
    <rect x="6" y="6" width="14" height="14" rx="2" />
    <path d="M17 3H5a2 2 0 0 0-2 2v12" />
  </Glyph>
);

export const ThemesGlyph = () => (
  <Glyph>
    <path d="M12 3a9 9 0 1 0 0 18c1 0 1.7-.8 1.7-1.7 0-.5-.2-.9-.5-1.2-.3-.3-.4-.7-.4-1.1 0-1 .8-1.7 1.7-1.7H16a5 5 0 0 0 5-5c0-3.9-4-7.3-9-7.3Z" />
    <path d="M7.5 12h.01M10 8h.01M14.5 8h.01" />
  </Glyph>
);

/* ---------- Trading View group ------------------------------------------- */

export const MarketOverviewGlyph = () => (
  <Glyph>
    <path d="M3 17.5 8.5 11l3.5 3.5L21 5" />
    <path d="M21 10V5h-5" />
    <path d="M3 21h18" />
  </Glyph>
);

export const ExchangeConfigGlyph = () => (
  <Glyph>
    <path d="M4 7h9M4 17h6" />
    <circle cx="16" cy="7" r="2.5" />
    <circle cx="13" cy="17" r="2.5" />
    <path d="M18.5 17H20M18.5 7H20" />
  </Glyph>
);

export const OrdersGlyph = () => (
  <Glyph>
    <path d="M4 5h2l1.6 9.3a2 2 0 0 0 2 1.7h6.8a2 2 0 0 0 2-1.6L20 8H7" />
    <circle cx="10" cy="19.5" r="1.3" />
    <circle cx="17" cy="19.5" r="1.3" />
  </Glyph>
);

export const AlertsGlyph = () => (
  <Glyph>
    <path d="M12 3.5 21 20H3l9-16.5Z" />
    <path d="M12 10v4M12 17h.01" />
  </Glyph>
);

export const BuySellGlyph = () => (
  <Glyph>
    <path d="M7 21V7M7 7 4 10M7 7l3 3" />
    <path d="M17 3v14M17 17l3-3M17 17l-3-3" />
  </Glyph>
);

export const DocCenterGlyph = () => (
  <Glyph>
    <path d="M4 6a2 2 0 0 1 2-2h3l2 2.5h7a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Z" />
    <path d="M9 13h6" />
  </Glyph>
);

export const RecurringGlyph = () => (
  <Glyph>
    <path d="M20 12a8 8 0 0 1-13.7 5.6M4 12a8 8 0 0 1 13.7-5.6" />
    <path d="M4 20v-4h4M20 4v4h-4" />
  </Glyph>
);

/* ---------- Wallet group ------------------------------------------------- */

/* The group is called Wallet and so is its main child, so the two rows are
   told apart by their icon alone — they cannot share one. */
export const BalancesGlyph = () => (
  <Glyph>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M6 10v4M18 10v4" />
  </Glyph>
);

export const SwapGlyph = () => (
  <Glyph>
    <path d="M4 8h12l-3.5-3.5M20 16H8l3.5 3.5" />
  </Glyph>
);

/* ---------- Notification group ------------------------------------------- */

export const ScheduleGlyph = () => (
  <Glyph>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
    <path d="M8 14h3" />
  </Glyph>
);

export const ClassGlyph = () => (
  <Glyph>
    <path d="M4 7.5 12 4l8 3.5-8 3.5-8-3.5Z" />
    <path d="M7 10v4.5c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5V10" />
    <path d="M20 8v5" />
  </Glyph>
);

export const OrchestratorGlyph = () => (
  <Glyph>
    <circle cx="6" cy="6" r="2.5" />
    <circle cx="18" cy="6" r="2.5" />
    <circle cx="12" cy="18" r="2.5" />
    <path d="M8.5 6h7M6 8.5v3a2 2 0 0 0 2 2h2M18 8.5v3a2 2 0 0 1-2 2h-2" />
  </Glyph>
);

/* ---------- Management group --------------------------------------------- */

export const UpdatesGlyph = () => (
  <Glyph>
    <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
    <path d="M8 9h6M8 13h8M8 17h5" />
  </Glyph>
);

export const NewsGlyph = () => (
  <Glyph>
    <path d="M3 6a1 1 0 0 1 1-1h13a1 1 0 0 1 1 1v12a2 2 0 0 0 2 2H5a2 2 0 0 1-2-2V6Z" />
    <path d="M18 9h2a1 1 0 0 1 1 1v8" />
    <path d="M6.5 8.5h7M6.5 12h7M6.5 15.5h4" />
  </Glyph>
);

export const AcademyGlyph = () => (
  <Glyph>
    <path d="M3 9.5 12 5l9 4.5-9 4.5-9-4.5Z" />
    <path d="M7 11.8V16c0 1.3 2.2 2.4 5 2.4s5-1.1 5-2.4v-4.2" />
  </Glyph>
);

export const ProReportGlyph = () => (
  <Glyph>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h5M8 12h8M8 16h6" />
    <path d="m15.5 6 1 1 2-2" />
  </Glyph>
);

export const RecommendationGlyph = () => (
  <Glyph d="m12 4 2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4L4.2 9.7l5.4-.8L12 4Z" />
);

export const SimulatorGlyph = () => (
  <Glyph>
    <rect x="3" y="5" width="18" height="12" rx="2" />
    <path d="M8 21h8M12 17v4" />
    <path d="m7 12 2.5-2.5L12 12l4-4" />
  </Glyph>
);

/* ---------- Sales Agent Hub group ---------------------------------------- */

export const InteractionsGlyph = () => (
  <Glyph>
    <path d="M4 5h9a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H8l-4 3V5Z" />
    <path d="M17 9h3v9l-3-2.5h-4a2 2 0 0 1-2-2" />
  </Glyph>
);

export const PerformanceGlyph = () => (
  <Glyph>
    <path d="M4 19a8 8 0 1 1 16 0" />
    <path d="m12 19 4-6" />
    <path d="M4 19h16" />
  </Glyph>
);

export const CallListGlyph = () => (
  <Glyph>
    <path d="M4.5 6.5c0 7 5 12 12 12l2-2.5-3.5-2-1.7 1.7a12.6 12.6 0 0 1-4.5-4.5L10.5 9.5l-2-3.5-2 .5Z" />
    <path d="M15 4h6M15 7.5h4" />
  </Glyph>
);

export const ProspectGlyph = () => (
  <Glyph>
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="m15 15 5 5" />
    <path d="M10.5 8v5M8 10.5h5" />
  </Glyph>
);

/* ---------- Marketing / Listings / Settings ------------------------------ */

export const AffiliatesGlyph = () => (
  <Glyph>
    <circle cx="7" cy="7" r="2.5" />
    <circle cx="17" cy="17" r="2.5" />
    <path d="M9.5 7H14a3 3 0 0 1 3 3v4.5" />
    <path d="M7 9.5V14a3 3 0 0 0 3 3h4.5" />
  </Glyph>
);

export const ListingOverviewGlyph = () => (
  <Glyph>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 9h18" />
    <path d="M7 13h4M7 16h7" />
  </Glyph>
);

export const ListingReportGlyph = () => (
  <Glyph>
    <path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9l-6-6Z" />
    <path d="M13 3v6h6" />
    <path d="M9 17v-3M12 17v-5M15 17v-2" />
  </Glyph>
);

export const AccessCodeGlyph = () => (
  <Glyph>
    <circle cx="8" cy="12" r="3.5" />
    <path d="M11.5 12H21l-1.5 2.5M17 12v3" />
  </Glyph>
);

export const DepositGlyph = () => (
  <Glyph>
    <path d="M12 3v10M12 13l-3.5-3.5M12 13l3.5-3.5" />
    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </Glyph>
);

export const RewardSchemeGlyph = () => (
  <Glyph>
    <circle cx="12" cy="9" r="5.5" />
    <path d="m8.5 13.5-1.5 7 5-2.5 5 2.5-1.5-7" />
  </Glyph>
);

export const RewardBankGlyph = () => (
  <Glyph>
    <path d="M3 9.5 12 4l9 5.5" />
    <path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" />
    <path d="M3 20.5h18" />
  </Glyph>
);

export const PlatformGlyph = () => (
  <Glyph>
    <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
    <path d="M8 20.5h8M12 16.5v4" />
    <path d="M9 10h6" />
  </Glyph>
);

export const BankWireGlyph = () => (
  <Glyph>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <path d="M3 10h18" />
    <path d="M7 14.5h4" />
  </Glyph>
);

export const CryptoGlyph = () => (
  <Glyph>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M9.5 8.5h4a2.2 2.2 0 0 1 0 4.4h-4M9.5 12.9h4.4a2.2 2.2 0 0 1 0 4.4H9.5M9.5 8.5v8.8M11.5 6.5v2M11.5 17.3v2" />
  </Glyph>
);

/* ---------- Savings group ------------------------------------------------ */

export const SavingsOverviewGlyph = () => (
  <Glyph>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M12 12 12 4a8 8 0 0 1 6.9 4L12 12Z" />
    <path d="M12 12l5.6 5.6" />
  </Glyph>
);

export const OrganizationsGlyph = () => (
  <Glyph>
    <rect x="3" y="8" width="8" height="12" rx="1.5" />
    <rect x="13" y="4" width="8" height="16" rx="1.5" />
    <path d="M6 12h2M6 16h2M16 8h2M16 12h2M16 16h2" />
  </Glyph>
);

export const ProductsGlyph = () => (
  <Glyph>
    <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
    <path d="m4 7.5 8 4.5 8-4.5M12 12v9" />
  </Glyph>
);
